import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import Select from 'react-select';
import PaysModal from '../pays/PaysModal';
import ParentModal from '../parents/ParentModal';

const sexeOptions = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' }
];
const typeSejourOptions = [
  { value: 'externe', label: 'Externe' },
  { value: 'interne', label: 'Interne' }
];

const EleveModal = ({ eleve, onClose, onSuccess }) => {
  const { register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm();
  const [parents, setParents] = useState([]);
  const [selectedParents, setSelectedParents] = useState([]);
  const [initialParents, setInitialParents] = useState([]); // Pour différencier ajouts/suppressions
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(eleve?.photo || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [paysList, setPaysList] = useState([]);
  const [showPaysModal, setShowPaysModal] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);

  useEffect(() => {
    const fetchParents = async () => {
      // Récupérer tous les parents sans pagination (limite très haute)
      const res = await api.get('/parents?limit=1000');
      setParents(res.data.data || []);
    };
    const fetchPays = async () => {
      const res = await api.get('/pays');
      setPaysList(res.data.data || []);
    };
    fetchParents();
    fetchPays();
    if (eleve) {
      reset(eleve);
      setPhotoPreview(eleve.photo || '');
      // Charger les parents liés à l'élève
      api.get(`/eleves/${eleve.id}`).then(res => {
        const parentIds = (res.data.data.parents || []).map(p => String(p.id));
        setSelectedParents(parentIds);
        setInitialParents(parentIds);
      });
    } else {
      reset({});
      setPhotoPreview('');
      setSelectedParents([]);
      setInitialParents([]);
    }
  }, [eleve, reset]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPhotoFile(null);
      setPhotoPreview('');
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview('');
    setValue('photo', '');
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let eleveId = eleve ? eleve.id : null;
      if (photoFile) {
        data.photo = photoPreview;
      }
      if (eleve) {
        await api.put(`/eleves/${eleve.id}`, data);
      } else {
        const res = await api.post('/eleves', data);
        eleveId = res.data.id;
      }
      // Gestion des liaisons parents
      // Ajouts
      const toAdd = selectedParents.filter(pid => !initialParents.includes(pid));
      // Suppressions
      const toRemove = initialParents.filter(pid => !selectedParents.includes(pid));
      await Promise.all([
        ...toAdd.map(parentId => api.post(`/parents/${parentId}/link-eleve`, { eleve_id: eleveId })),
        ...toRemove.map(parentId => api.delete(`/parents/${parentId}/unlink-eleve/${eleveId}`)),
      ]);
      onSuccess();
    } catch (e) {
      // Gérer l'erreur
    } finally {
      setLoading(false);
    }
  };

  // Options react-select pour les pays
  const paysOptions = paysList.map(pays => ({ value: pays.nom_pays, label: pays.nom_pays }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-6xl">
        <h2 className="text-xl font-semibold mb-4">
          {eleve ? 'Modifier' : 'Nouvel'} élève
        </h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset className="mb-6 border-b pb-4">
            <legend className="text-lg font-semibold mb-2">Informations élève</legend>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input {...register('nom', { required: true })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                {errors.nom && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                <input {...register('prenom', { required: true })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                {errors.prenom && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sexe *</label>
                <Select
                  options={sexeOptions}
                  value={sexeOptions.find(opt => opt.value === watch('sexe')) || null}
                  onChange={opt => setValue('sexe', opt ? opt.value : '')}
                  isClearable
                  placeholder="Sélectionner"
                  classNamePrefix="react-select"
                />
                {errors.sexe && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance *</label>
                <input type="date" {...register('date_naissance', { required: true })} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                {errors.date_naissance && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de séjour *</label>
                <Select
                  options={typeSejourOptions}
                  value={typeSejourOptions.find(opt => opt.value === watch('type_sejour')) || null}
                  onChange={opt => setValue('type_sejour', opt ? opt.value : '')}
                  isClearable
                  placeholder="Sélectionner"
                  classNamePrefix="react-select"
                />
                {errors.type_sejour && <span className="text-red-500 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {photoPreview && (
                  <div className="mt-2">
                    <img src={photoPreview} alt="Aperçu" className="h-24 rounded shadow" />
                    <button type="button" className="text-xs text-red-600 mt-1" onClick={removePhoto}>Supprimer</button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lieu de naissance</label>
                <input {...register('lieu_naissance')} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select
                      options={paysOptions}
                      value={paysOptions.find(opt => opt.value === watch('nationalite')) || null}
                      onChange={opt => setValue('nationalite', opt ? opt.value : '')}
                      isClearable
                      placeholder="Sélectionner un pays"
                      classNamePrefix="react-select"
                    />
                  </div>
                  <button
                    type="button"
                    className="px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700"
                    onClick={() => setShowPaysModal(true)}
                    title="Ajouter un pays"
                  >+
                  </button>
                </div>
                {showPaysModal && (
                  <PaysModal
                    onClose={() => setShowPaysModal(false)}
                    onSuccess={async (newPays) => {
                      setShowPaysModal(false);
                      const res = await api.get('/pays');
                      setPaysList(res.data.data || []);
                      if (newPays && newPays.nom_pays) {
                        setValue('nationalite', newPays.nom_pays, { shouldValidate: true });
                      }
                    }}
                  />
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input {...register('adresse')} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input {...register('telephone')} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" {...register('email')} className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
            </div>
          </fieldset>

          <fieldset className="mb-6">
            <legend className="text-lg font-semibold mb-2">Parents</legend>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lier des parents</label>
              <Select
                isMulti
                isSearchable
                options={parents.map(parent => ({
                  value: String(parent.id),
                  label: `${parent.nom} ${parent.prenom} (${parent.type_parente})`
                }))}
                value={parents
                  .filter(parent => selectedParents.includes(String(parent.id)))
                  .map(parent => ({
                    value: String(parent.id),
                    label: `${parent.nom} ${parent.prenom} (${parent.type_parente})`
                  }))}
                onChange={opts => setSelectedParents(opts.map(opt => opt.value))}
                placeholder="Rechercher ou sélectionner un parent"
                classNamePrefix="react-select"
              />
              <button type="button" className="mt-2 text-primary-600 hover:underline" onClick={() => setShowParentModal(true)}>
                + Ajouter un parent
              </button>
              <div className="mt-2 flex flex-wrap gap-2">
                  {selectedParents.map(pid => {
                    const p = parents.find(par => String(par.id) === pid);
                    if (!p) return null;
                    let badgeClass = "bg-gray-200 text-gray-800";
                    if (p.type_parente && p.type_parente.toLowerCase().includes("père")) {
                      badgeClass = "bg-blue-100 text-blue-800 border border-blue-300";
                    } else if (p.type_parente && p.type_parente.toLowerCase().includes("mère")) {
                      badgeClass = "bg-pink-100 text-pink-800 border border-pink-300";
                    }
                    return (
                      <span key={pid} className={`px-2 py-1 rounded text-sm font-medium ${badgeClass}`}>
                        {p.nom} {p.prenom} ({p.type_parente})
                      </span>
                    );
                  })}
              </div>
            </div>
          </fieldset>

          <div className="flex justify-end space-x-2">
            <button type="button" className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300" onClick={onClose}>Annuler</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700" disabled={loading}>
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
        {showPaysModal && (
          <PaysModal
            onClose={() => setShowPaysModal(false)}
            onSuccess={async (newPays) => {
              setShowPaysModal(false);
              const res = await api.get('/pays');
              setPaysList(res.data.data || []);
              if (newPays && newPays.nom_pays) {
                setValue('nationalite', newPays.nom_pays);
              }
            }}
          />
        )}
        {showParentModal && (
          <ParentModal
            onClose={() => setShowParentModal(false)}
            onSuccess={async (newParent) => {
              setShowParentModal(false);
              const res = await api.get('/parents');
              setParents(res.data.data || []);
              if (newParent && newParent.id) {
                setSelectedParents(prev => [...prev, String(newParent.id)]);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EleveModal;
